import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useStoreValue } from 'store';
import { LayoutPage, LayoutContent, Form, FormRenderer, Nudge } from 'components';
import { config, STEP_ABOUT_YOU } from '_config';
import { initFormConfig, isAU, submitHandler, byCountry } from 'utils';
import { sanitizeDvsConsent } from 'utils/dvsConsent';
import { useEventTracking } from 'react-event-tracker';
import { getDataLayerElements } from 'utils/getDataLayerElements';
import { useFormInitialValues, useQueryParam, useThreatMetrix } from 'hooks';
import { ConsentMessage } from './PersonalDetails.style';

const FORM_ID = 'personalDetails';
function PersonalDetails() {
  const [storeState, updateStore] = useStoreValue();
  const { acquisition, ...states } = storeState;
  const formConfig = useMemo(() => initFormConfig(config.formConfigs[FORM_ID], storeState), [storeState]);

  useThreatMetrix({ disabled: false });
  const history = useHistory();
  const { trackEvent } = useEventTracking();
  const initialValues = useFormInitialValues(FORM_ID);
  const isBiometricsCancelled = useQueryParam('cancel');

  useEffect(() => {
    if (isBiometricsCancelled) {
      // biometrics cancellation will redirect to this page
      updateStore({
        biometricsVerification: 'CANCELLED',
        activeStep: STEP_ABOUT_YOU,
        biometricsResult: null,
      });
    }
  }, [isBiometricsCancelled, updateStore]);

  const onSubmit = ({ values, errors }) => {
    function updateStoreAndRedirect() {
      const personalDetails = sanitizeDvsConsent(values);
      const newAcquisition = {
        ...acquisition,
        personalDetails,
      };

      if (isAU()) {
        newAcquisition.extraDetails.idType = 'AU_DRIVERS_LICENCE';
      }

      updateStore({
        activeStep: '/address-details',
        acquisition: newAcquisition,
      });

      history.push('/address-details');

      trackEvent({
        event: {
          category: 'application',
          action: 'application-navigation',
          location: 'about-you',
          label: byCountry({
            AU: 'Next',
            NZ: 'Next',
          }),
        },
        ...getDataLayerElements({ acquisition: newAcquisition, ...states }),
      });
    }

    submitHandler({ submit: updateStoreAndRedirect, errors });
  };

  return (
    <LayoutPage noSessionTimeout>
      <ConsentMessage>
        <Form
          id={FORM_ID}
          initialValues={initialValues}
          onSubmit={onSubmit}
          showSaveDraftButton
          saveDraftOnSubmit
          saveDraftOnSessionTimeout
          showBackButton={false}
          transformValues={sanitizeDvsConsent}
        >
          {formData => {
            return (
              <LayoutContent>
                <Nudge />
                <FormRenderer config={formConfig} formData={formData} />
              </LayoutContent>
            );
          }}
        </Form>
      </ConsentMessage>
    </LayoutPage>
  );
}

export { PersonalDetails };
